import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getArticles, getCategories, getCategoryImage, getGroepPaginas, getProduct, ownArticleNumber, soortSlug, type Soort } from "@/lib/catalog";
import { glassColorPhoto } from "@/lib/glass-photos";
import { cleanArticleName, effectiveArticleNumber, effectiveName, effectivePricePerM2, isDisabled, type CatalogOverrides } from "@glastotaal/core";
import { useSoorten } from "@/lib/catalog-view";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { priceWithSale, usePromotions, type PromoMap } from "@/lib/promotions";
import { useVariantIndex, variantGtNumber, variantHref, type VariantEntry } from "@/lib/variant-search";
import { ARTICLES, localizeArticle } from "@/features/knowledge/articles";
import { formatEuro } from "@/lib/format";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
import { cdnImage, IMG } from "@/lib/img";
import { useT, useLocale, useCatalogVersion, translate, type Locale } from "@/lib/i18n";
import { trackSearch } from "@/lib/track";
import { toonbaarSysteem } from "@/lib/systemen";
import { termScore } from "@/lib/zoektermen";

type ItemType = "soort" | "kleur" | "categorie" | "groep" | "product" | "kennisbank" | "pagina";

interface SearchItem {
  id: string;
  type: ItemType;
  title: string;
  subtitle: string;
  href: string;
  imageUrl?: string;
  fromCents?: number;
  /** Originele prijs (doorstreept) als er een actie loopt. */
  wasCents?: number;
  onRequest?: boolean;
}

/** Volgorde + labels + max per sectie in de dropdown. */
const GROUPS: { type: ItemType; label: string; cap: number }[] = [
  { type: "soort", label: "Glassoorten", cap: 3 },
  { type: "kleur", label: "Glaskleuren & uitvoeringen", cap: 5 },
  { type: "categorie", label: "Categorieën", cap: 3 },
  // Productgroepen ("Kitten", "Glaszuigers") staan bewust BOVEN de losse producten: wie "kit"
  // zoekt wil meestal het hele schap zien, niet het eerste kitmes dat toevallig zo heet.
  { type: "groep", label: "Productgroepen", cap: 5 },
  { type: "product", label: "Producten", cap: 5 },
  { type: "kennisbank", label: "Kennisbank", cap: 3 },
  { type: "pagina", label: "Pagina's", cap: 2 },
];

/** Client-side index: glassoorten + categorieën + producten + varianten + kennisbank + kernpagina's. */
function buildIndex(soortenIn: Soort[], variants: VariantEntry[], ov: CatalogOverrides, promos: PromoMap, locale: Locale): SearchItem[] {
  const soorten: SearchItem[] = soortenIn.map((s) => ({
    id: `soort-${s.productSlug}-${s.subcategory}`,
    type: "soort",
    title: s.subcategory,
    subtitle: `${s.productName} · glas op maat`,
    href: `/glas-op-maat/${soortSlug(s)}`,
    imageUrl: s.img,
    fromCents: s.vanafCents,
  }));
  // Glaskleuren/uitvoeringen per soort (chinchilla, acide groen, crepi …) → zoekbaar en
  // doorlinkend naar de configurator met díe kleur voorgeselecteerd (?kleur=). Zo vind je
  // "chinchilla" en land je bij Figuurglas met chinchilla al gekozen.
  const glaskleuren: SearchItem[] = soortenIn.flatMap((s) => {
    const slug = soortSlug(s);
    const seen = new Set<string>();
    const out: SearchItem[] = [];
    for (const v of getProduct(s.productSlug)?.variants ?? []) {
      const color = v.color;
      if (!color || v.subcategory !== s.subcategory || seen.has(color)) continue;
      seen.add(color);
      out.push({
        id: `kleur-${s.productSlug}-${s.subcategory}-${color}`,
        type: "kleur",
        title: color,
        subtitle: `${translate(s.subcategory, locale)} · ${translate("glas op maat", locale)}`,
        href: `/glas-op-maat/${slug}?kleur=${encodeURIComponent(color)}`,
        imageUrl: glassColorPhoto(slug, color) ?? s.img,
      });
    }
    return out;
  });
  // Groep- en subgroeppagina's (169 stuks) — elk met een eigen adres sinds de SEO-slag.
  const groepen: SearchItem[] = getGroepPaginas().map((g) => ({
    id: g.id,
    type: "groep",
    title: g.label,
    subtitle: `${translate(g.pad, locale)} · ${g.count} ${translate(g.count === 1 ? "product" : "producten", locale)}`,
    href: g.href,
    imageUrl: g.image,
  }));
  const categorieen: SearchItem[] = getCategories().map((c) => ({
    id: `cat-${c.slug}`,
    type: "categorie",
    title: c.name,
    subtitle: "Categorie",
    href: `/categorie/${c.slug}`,
    imageUrl: getCategoryImage(c.slug),
  }));
  const products: SearchItem[] = getArticles()
    .filter((a) => !isDisabled(a.slug, ov))
    .map((a) => ({
      id: `prod-${a.slug}`,
      type: "product",
      title: effectiveName(a.slug, cleanArticleName(a.name, a.articleNumber), ov),
      // Systeem meenemen zodat zoeken op "70KLIK" ook het PROFIEL vindt en niet alleen de
      // onderdelen — die dragen de code in hun naam, het profiel alleen in het (niet-getoonde)
      // leveranciersnummer.
      subtitle: [a.merk, translate(a.groep ?? "", locale), effectiveArticleNumber(a.slug, ownArticleNumber(a.slug, a.articleNumber), ov), toonbaarSysteem(a, ov)].filter(Boolean).join(" · "),
      href: `/artikel/${a.slug}`,
      imageUrl: a.image,
      onRequest: a.onRequest,
      ...(a.onRequest
        ? { fromCents: undefined }
        : (() => {
            const { priceCents, wasCents } = priceWithSale(effectivePricePerM2(a.priceCents, a.slug, ov, [a.category]), promos[a.slug]);
            return { fromCents: priceCents, wasCents: wasCents ?? undefined };
          })()),
    }));
  const articles: SearchItem[] = ARTICLES.map((a) => {
    const loc = localizeArticle(a, locale);
    return {
      id: `art-${a.slug}`,
      type: "kennisbank" as const,
      title: loc.title,
      subtitle: `${translate("Kennisbank", locale)} · ${translate(a.category, locale)}`,
      href: `/kennisbank/${a.slug}`,
      imageUrl: a.image,
    };
  });
  // Sub-varianten: elk als eigen zoekbaar product, doorlinkend naar de productpagina met de
  // variant voorgeselecteerd. Zoekbaar op naam, eigen GT-nummer én het Timmers-artikelnummer.
  const variantItems: SearchItem[] = variants
    .filter((v) => !isDisabled(v.slug, ov))
    .map((v) => {
      const { priceCents, wasCents } = priceWithSale(effectivePricePerM2(v.priceCents, v.slug, ov, [v.category]), promos[v.slug]);
      return {
        id: `var-${v.slug}`,
        type: "product" as const,
        title: effectiveName(v.slug, cleanArticleName(v.name, v.articleNumber), ov),
        subtitle: [v.merk, translate(v.groep ?? "", locale), variantGtNumber(v), v.articleNumber].filter(Boolean).join(" · "),
        href: variantHref(v),
        imageUrl: v.image || undefined,
        fromCents: priceCents,
        wasCents: wasCents ?? undefined,
      };
    });
  const pages: SearchItem[] = [
    { id: "p-kennis", type: "pagina", title: "Kennisbank", subtitle: "Gidsen en uitleg", href: "/kennisbank" },
    { id: "p-lever", type: "pagina", title: "Levertijden & verzending", subtitle: "Klantenservice", href: "/levertijden" },
    { id: "p-herroep", type: "pagina", title: "Herroepingsrecht", subtitle: "Klantenservice", href: "/herroepingsrecht" },
    { id: "p-garantie", type: "pagina", title: "Garantie & transportschade", subtitle: "Klantenservice", href: "/garantie" },
    { id: "p-contact", type: "pagina", title: "Contact", subtitle: "Klantenservice", href: "/contact" },
  ];
  return [...soorten, ...glaskleuren, ...categorieen, ...groepen, ...products, ...variantItems, ...articles, ...pages];
}

function rank(items: SearchItem[], query: string): SearchItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const words = q.split(/\s+/);
  return items
    .map((item) => {
      let score = 0;
      for (const w of words) {
        // De titel weegt vol, de ondertitel (merk, groep, artikelnummer) half: een treffer in de
        // naam is sterker bewijs dan een treffer in de metaregel eronder.
        const inTitel = termScore(item.title, w);
        const inSub = termScore(item.subtitle, w);
        if (!inTitel && !inSub) return null;
        score += Math.max(inTitel, inSub / 2);
      }
      return { item, score };
    })
    .filter((r): r is { item: SearchItem; score: number } => r !== null)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item);
}

const ICON: Record<ItemType, string> = { soort: "Glassoort", kleur: "Glaskleur", categorie: "Categorie", groep: "Groep", product: "Product", kennisbank: "Kennisbank", pagina: "Pagina" };

/** Header-zoek met een gegroepeerde suggestie-dropdown + doorklik naar /zoeken. */
export function SearchBar() {
  const t = useT();
  const [locale] = useLocale();
  const catV = useCatalogVersion();
  const navigate = useNavigate();
  const soorten = useSoorten();
  const ov = useCatalogOverrides();
  const promos = usePromotions();
  const [pref] = useBtwPreference();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Variant-index lazy laden zodra er daadwerkelijk gezocht wordt (aparte chunk).
  const variants = useVariantIndex(query.trim().length > 0);
  const index = useMemo(() => buildIndex(soorten, variants, ov, promos, locale), [soorten, variants, ov, promos, locale, catV]);
  const wrap = useRef<HTMLDivElement>(null);
  // Het zoekvak verlaten telt als "zoekopdracht afgerond". Via een ref, omdat de luisteraars
  // hieronder maar één keer worden opgehangen en anders de afsluiter van de eerste render houden.
  const sluitRef = useRef<() => void>(() => {});

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) sluitRef.current();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") sluitRef.current();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const ranked = useMemo(() => rank(index, query), [index, query]);
  // Groepeer per type met een maximum per sectie.
  const sections = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: ranked.filter((r) => r.type === g.type).slice(0, g.cap) })).filter((g) => g.items.length > 0),
    [ranked],
  );
  const showPanel = open && query.trim().length > 0;

  // Een zoekopdracht wordt gemeten als hij AF is, niet als het typen even stilvalt.
  //
  // Hiervoor ging er een event uit na 1,8 seconde pauze. Dat leek voorzichtig genoeg, maar wie
  // halverwege nadenkt stuurt alsnog een halve term: in GA4 stonden "Kuns", "Kunststof",
  // "Kunststof a", "Kunststof afde" en "Kunststof gl" naast elkaar als vijf losse zoekopdrachten.
  // Over dertig dagen werden 164 zoekopdrachten zo 157 verschillende termen van elk één keer —
  // een rapport waar geen conclusie uit te trekken valt. Een langere wachttijd lost dat niet op;
  // de pauze is simpelweg geen einde.
  //
  // Afgeronde momenten zijn er drie: Enter, een suggestie aanklikken, en het zoekvak verlaten.
  // Dat laatste hoort erbij: wie zoekt, niets bruikbaars ziet en wegklikt is juist de interessante
  // meting — daar zit wat we niet verkopen of niet vindbaar hebben gemaakt.
  const gemetenTermen = useRef<Set<string>>(new Set());
  // Refs, zodat de afsluit-handler hieronder (die maar één keer wordt opgehangen) altijd de
  // actuele term en het actuele aantal resultaten leest in plaats van die van de eerste render.
  const queryRef = useRef(query);
  queryRef.current = query;
  // Alleen meten als er ook echt gezocht werd: een klik buiten een gesloten zoekvak is geen
  // afgeronde zoekopdracht.
  const openRef = useRef(showPanel);
  openRef.current = showPanel;
  const rankedRef = useRef(ranked.length);
  rankedRef.current = ranked.length;
  const meetNu = (q: string): void => {
    const sleutel = q.trim().toLowerCase();
    if (sleutel.length < 3 || gemetenTermen.current.has(sleutel)) return;
    gemetenTermen.current.add(sleutel);
    trackSearch(q.trim(), rankedRef.current);
  };
  /** Afsluiten: meet de term zoals hij er nu staat, en klap het paneel dicht. */
  sluitRef.current = (): void => {
    if (openRef.current) meetNu(queryRef.current);
    setOpen(false);
  };

  const goToResults = (): void => {
    const q = query.trim();
    if (!q) return;
    meetNu(q);
    setOpen(false);
    navigate(`/zoeken?q=${encodeURIComponent(q)}`);
  };
  const select = (item: SearchItem): void => {
    meetNu(query);
    setOpen(false);
    setQuery("");
    navigate(item.href);
  };

  return (
    <div ref={wrap} className="relative w-full">
      <form
        onSubmit={(e) => { e.preventDefault(); goToResults(); }}
        className={`flex items-center border border-primary-100 bg-primary-50 transition focus-within:border-primary-300 focus-within:bg-white ${showPanel ? "bg-white" : ""}`}
      >
        <button
          type="submit"
          aria-label={t("Zoeken")}
          className="flex h-11 w-10 shrink-0 items-center justify-center text-primary-600 transition hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:h-10"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.5" y2="16.5" />
          </svg>
        </button>
        <input
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("Waar bent u naar op zoek?")}
          aria-label={t("Zoeken")}
          autoComplete="off"
          className="min-h-11 w-full bg-transparent px-2.5 text-base text-primary-900 placeholder-primary-400 outline-none sm:min-h-10 sm:text-sm [&::-webkit-search-cancel-button]:appearance-none"
        />
      </form>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-50 max-h-[calc(100dvh-170px)] overflow-y-auto overscroll-contain border border-t-0 border-primary-200 bg-white shadow-xl sm:max-h-[70vh]">
          {sections.length === 0 ? (
            <p className="p-5 text-center text-sm text-primary-600">{t("Geen resultaten voor")} "{query}".</p>
          ) : (
            <>
              {sections.map((sec) => (
                <div key={sec.type} className="border-b border-primary-50 last:border-0">
                  <p className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-primary-600">{t(sec.label)}</p>
                  <ul>
                    {sec.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => select(item)}
                          className="group flex w-full items-center gap-3 px-4 py-2 text-left transition hover:bg-primary-50"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden border border-primary-100 bg-primary-50 text-[9px] text-primary-300">
                            {item.imageUrl ? <img src={cdnImage(item.imageUrl, { w: IMG.thumb })} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" /> : ICON[item.type].slice(0, 3)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-primary-900 group-hover:text-primary-700">{t(item.title)}</span>
                            <span className="block truncate text-xs text-primary-600">{t(item.subtitle)}</span>
                          </span>
                          {item.type === "soort" && item.fromCents != null && (
                            <span className="shrink-0 text-right text-xs text-primary-600">{t("vanaf")} <strong className="text-accent-700">{formatEuro(btwAmount(item.fromCents, pref))}</strong>/m² {btwLabel(pref)}</span>
                          )}
                          {item.type === "product" && item.fromCents != null && (
                            <span className="shrink-0 text-right text-xs">
                              {item.wasCents != null && <span className="mr-1 text-primary-600 line-through">{formatEuro(btwAmount(item.wasCents, pref))}</span>}
                              <span className={`font-semibold ${item.wasCents != null ? "text-accent-700" : "text-primary-700"}`}>{formatEuro(btwAmount(item.fromCents, pref))}</span>
                              <span className="ml-1 font-normal text-primary-600">{btwLabel(pref)}</span>
                            </span>
                          )}
                          {item.type === "product" && item.onRequest && (
                            <span className="shrink-0 text-right text-xs italic text-primary-600">{t("Prijs op aanvraag")}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <button
                type="button"
                onClick={goToResults}
                className="flex w-full items-center justify-between bg-primary-50/60 px-4 py-3 text-sm font-semibold text-accent-700 transition hover:bg-primary-50"
              >
                <span>{t("Toon alle resultaten voor")} "{query.trim()}"</span>
                <span aria-hidden="true">→</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
